import { tileCenter, unitsToTile } from '../core/fixed';
import { nearestFootprintTile, type Building } from '../entities/building';
import { NEUTRAL_OWNER } from '../entities/player';
import type { Unit } from '../entities/unit';
import type { GameMap } from '../map/gameMap';
import type { Pathfinder } from '../map/pathfinding';
import type { OccupancyFn, UnitGrid } from '../map/unitGrid';
import { planPath } from './movement';

/** Ticks a dead unit lingers (for the fade-out) before removal. */
export const DYING_TICKS = 10;

/** What the combat system needs from the simulation. `Sim` satisfies this. */
export interface CombatContext {
  readonly map: GameMap;
  readonly pathfinder: Pathfinder;
  readonly unitGrid: UnitGrid;
  getUnit(id: number): Unit | undefined;
  getBuilding(id: number): Building | undefined;
  occupancyFor(unit: Unit): OccupancyFn;
  killUnit(unit: Unit): void;
  destroyBuilding(b: Building): void;
}

type Target =
  | { readonly kind: 'unit'; readonly unit: Unit }
  | { readonly kind: 'building'; readonly building: Building };

/** A living, attackable enemy of `attacker` with this id, or null. */
function resolveTarget(ctx: CombatContext, attacker: Unit, id: number): Target | null {
  const u = ctx.getUnit(id);
  if (u) {
    if (u.owner === attacker.owner || u.state.kind === 'dying' || u.hidden) return null;
    return { kind: 'unit', unit: u };
  }
  const b = ctx.getBuilding(id);
  if (b && b.hp !== null && b.owner !== attacker.owner && b.owner !== NEUTRAL_OWNER)
    return { kind: 'building', building: b };
  return null;
}

/** Tile of the target nearest to the attacker (the unit's tile, or the closest footprint tile). */
function targetTile(attacker: Unit, t: Target): { x: number; y: number } {
  const ax = unitsToTile(attacker.x);
  const ay = unitsToTile(attacker.y);
  return t.kind === 'unit'
    ? { x: unitsToTile(t.unit.x), y: unitsToTile(t.unit.y) }
    : nearestFootprintTile(t.building, ax, ay);
}

function tileDistance(attacker: Unit, t: Target): number {
  const tt = targetTile(attacker, t);
  return Math.max(
    Math.abs(unitsToTile(attacker.x) - tt.x),
    Math.abs(unitsToTile(attacker.y) - tt.y),
  );
}

function walkToward(ctx: CombatContext, attacker: Unit, t: Target): { x: number; y: number } {
  const range = attacker.def.combat?.range ?? 1;
  const tt = targetTile(attacker, t);
  attacker.path = planPath(attacker, ctx.map, ctx.pathfinder, tileCenter(tt.x), tileCenter(tt.y), {
    occupancy: ctx.occupancyFor(attacker),
    accept: (tx, ty) => {
      // Any tile from which the target is in reach.
      const near = t.kind === 'unit' ? tt : nearestFootprintTile(t.building, tx, ty);
      return Math.max(Math.abs(tx - near.x), Math.abs(ty - near.y)) <= range;
    },
  });
  return tt;
}

/** Order `attacker` to attack entity `targetId`. Returns false when it cannot. */
export function beginAttack(ctx: CombatContext, attacker: Unit, targetId: number): boolean {
  if (!attacker.def.combat || attacker.state.kind === 'dying') return false;
  const t = resolveTarget(ctx, attacker, targetId);
  if (!t) return false;
  attacker.hidden = false;
  const tile = walkToward(ctx, attacker, t);
  attacker.state = { kind: 'attacking', targetId, cooldownLeft: 0, targetTile: tile };
  return true;
}

/** Runs after movement each tick for units in the 'attacking' state. */
export function stepCombat(ctx: CombatContext, attacker: Unit): void {
  if (attacker.state.kind !== 'attacking' || !attacker.def.combat) return;
  const { combat } = attacker.def;
  const t = resolveTarget(ctx, attacker, attacker.state.targetId);
  if (!t) {
    attacker.path = null;
    attacker.state = { kind: 'idle' };
    return;
  }

  if (tileDistance(attacker, t) <= combat.range) {
    attacker.path = null; // in reach: stand and fight
    if (attacker.state.cooldownLeft > 0) {
      attacker.state = { ...attacker.state, cooldownLeft: attacker.state.cooldownLeft - 1 };
      return;
    }
    if (t.kind === 'unit') {
      t.unit.hp -= combat.damage;
      if (t.unit.hp <= 0) ctx.killUnit(t.unit);
    } else if (t.building.hp !== null) {
      t.building.hp -= combat.damage;
      if (t.building.hp <= 0) ctx.destroyBuilding(t.building);
    }
    attacker.state = { ...attacker.state, cooldownLeft: combat.cooldownTicks };
    return;
  }

  // Out of reach: chase. Re-plan when the path ran out or the target changed tile.
  const now = targetTile(attacker, t);
  const planned = attacker.state.targetTile;
  if (!attacker.path || !planned || planned.x !== now.x || planned.y !== now.y) {
    const tile = walkToward(ctx, attacker, t);
    attacker.state = { ...attacker.state, targetTile: tile };
    if (!attacker.path) {
      // Cannot get any closer; give up rather than stand forever.
      attacker.state = { kind: 'idle' };
    }
  }
}

/**
 * Nearest living enemy unit within the attacker's acquire range: by tile distance, then
 * lowest id. Uses the unit grid; never scans all units.
 */
export function acquireTarget(ctx: CombatContext, attacker: Unit): Unit | null {
  const combat = attacker.def.combat;
  if (!combat) return null;
  const ax = unitsToTile(attacker.x);
  const ay = unitsToTile(attacker.y);
  let best: Unit | null = null;
  let bestDist = Infinity;
  for (const id of ctx.unitGrid.unitsWithin(ax, ay, combat.acquireRange)) {
    const u = ctx.getUnit(id);
    if (!u || u.owner === attacker.owner || u.state.kind === 'dying' || u.hidden) continue;
    const d = Math.max(Math.abs(unitsToTile(u.x) - ax), Math.abs(unitsToTile(u.y) - ay));
    if (d < bestDist || (d === bestDist && best !== null && u.id < best.id)) {
      best = u;
      bestDist = d;
    }
  }
  return best;
}

/** Idle fighters defend themselves: pick a target if any enemy is close. */
export function stepAutoAcquire(ctx: CombatContext, unit: Unit): void {
  if (unit.state.kind !== 'idle' || !unit.def.combat || unit.hidden) return;
  const target = acquireTarget(ctx, unit);
  if (target) beginAttack(ctx, unit, target.id);
}

/** Count down a dying unit; returns true when it should be removed. */
export function stepDying(unit: Unit): boolean {
  if (unit.state.kind !== 'dying') return false;
  if (unit.state.ticksLeft <= 1) return true;
  unit.state = { kind: 'dying', ticksLeft: unit.state.ticksLeft - 1 };
  return false;
}
